import React from "react";
import { SectionLabel } from "@/components/Bits";
import { EMAIL, PHONE_RAW } from "@/lib/api";

// Inline helpers — keep styling consistent with the rest of the site.
const PHONE_DISPLAY = "+1 (646) 595-1666";

function Email() {
  return (
    <a
      href={`mailto:${EMAIL}`}
      className="text-forest-600 font-medium underline underline-offset-2 decoration-sage-300 hover:text-forest-500 transition-colors"
    >
      {EMAIL}
    </a>
  );
}

function PhoneLink() {
  return (
    <a
      href={`tel:${PHONE_RAW}`}
      className="text-forest-600 font-medium underline underline-offset-2 decoration-sage-300 hover:text-forest-500 transition-colors"
    >
      {PHONE_DISPLAY}
    </a>
  );
}

function Ext({ href, children }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="text-forest-600 font-medium underline underline-offset-2 decoration-sage-300 hover:text-forest-500 transition-colors"
    >
      {children}
    </a>
  );
}

function Section({ title, children }) {
  return (
    <section className="mt-12" data-reveal>
      <h2 className="font-display font-bold text-forest-600 text-2xl md:text-3xl leading-tight">{title}</h2>
      <div className="mt-4 space-y-4 text-forest-400 text-[15px] md:text-base leading-relaxed">{children}</div>
    </section>
  );
}

function Bullets({ children }) {
  return <ul className="list-disc pl-5 space-y-2 marker:text-sage-300">{children}</ul>;
}

const Term = ({ children }) => <strong className="text-forest-600 font-semibold">{children}</strong>;

export default function PrivacyPolicy() {
  return (
    <section className="max-w-[1320px] mx-auto px-5 lg:px-10 pt-10 lg:pt-16">
      <div className="max-w-3xl">
        {/* HERO */}
        <div data-reveal>
          <SectionLabel>Legal</SectionLabel>
          <h1 className="mt-6 font-display font-extrabold tracking-tight text-forest-600 text-4xl md:text-6xl leading-[1.0]">
            Privacy <span className="font-serif-italic font-normal">Policy</span>
          </h1>
          <p className="mt-5 text-sm text-forest-400/80">Last updated: June 9, 2026</p>
          <div className="mt-6 space-y-4 text-forest-400 text-[15px] md:text-base leading-relaxed">
            <p>
              Aidify (&ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;our&rdquo;) respects your privacy and is committed to
              protecting the personal information you share with us. This Privacy Policy explains what data we collect,
              how we use it, who we share it with, and the rights you have over your information.
            </p>
            <p>
              This policy applies to aidify.us and all related Aidify services, communications, and interactions. Aidify
              is a brand operated by ANS RPO Solutions, with its principal place of business in Delhi, India.
            </p>
          </div>
        </div>

        {/* 1 */}
        <Section title="1. Who We Are">
          <p>
            Aidify provides smart virtual assistance services — connecting businesses with technologically adept virtual
            assistants for administrative, eCommerce, customer support, bookkeeping, marketing, and operational tasks.
          </p>
          <p>
            <Term>Data Controller:</Term> ANS RPO Solutions (operating as Aidify). Registered office: Delhi, India.
            Email: <Email />. Phone: <PhoneLink />
          </p>
          <p>
            For privacy-related questions or to exercise your rights under this policy, contact us at <Email />.
          </p>
        </Section>

        {/* 2 */}
        <Section title="2. Information We Collect">
          <p>
            We collect information in three ways: information you give us directly, information collected automatically,
            and information from third parties.
          </p>
          <p>
            <Term>Information you provide directly:</Term>
          </p>
          <Bullets>
            <li>
              Contact and inquiry forms — name, email address, phone number, company name, country, and any details you
              share in your message
            </li>
            <li>
              Service booking and consultation requests — your stated needs, project type, preferred services, timezone,
              and budget range if disclosed
            </li>
            <li>Newsletter signup — email address</li>
            <li>
              Account registration (if applicable) — name, business email, company information, and account credentials
            </li>
            <li>
              Calendly bookings — name, email, scheduling preferences, and any information you choose to share when
              booking a consultation
            </li>
            <li>
              Engagement-related information — once you become a client, we collect information necessary to deliver
              services, including task instructions, access credentials you choose to share (managed securely via
              LastPass or equivalent), and project communications
            </li>
          </Bullets>
          <p>
            <Term>Information collected automatically:</Term>
          </p>
          <Bullets>
            <li>
              Usage and analytics data — pages visited, time spent on pages, links clicked, referring URLs, and general
              interaction patterns, collected via Google Analytics
            </li>
            <li>
              Device and technical data — IP address, browser type and version, operating system, device type, screen
              resolution, and language preferences
            </li>
            <li>Cookies and similar technologies — see Section 8 (Cookies) below</li>
          </Bullets>
          <p>
            <Term>Information from third parties:</Term>
          </p>
          <Bullets>
            <li>
              Social media platforms — if you interact with our pages on Facebook, Instagram, LinkedIn, or X, we may
              receive limited public profile information from those platforms
            </li>
            <li>
              Service partners — Calendly (booking data), WhatsApp Business (if you initiate contact via WhatsApp), and
              email service providers
            </li>
          </Bullets>
        </Section>

        {/* 3 */}
        <Section title="3. How We Use Your Information">
          <p>We use your information for the following purposes:</p>
          <Bullets>
            <li>To respond to your inquiries and provide the services you request</li>
            <li>To match you with the right virtual assistant based on your stated needs</li>
            <li>To deliver, manage, and improve our services during your engagement with Aidify</li>
            <li>
              To communicate with you about your account, services, updates, and — with your consent — marketing and
              promotional information
            </li>
            <li>To process payments and manage billing through our payment processors</li>
            <li>To improve our website, services, and user experience through analytics</li>
            <li>
              To comply with legal obligations including tax, accounting, and regulatory requirements under Indian and
              applicable international law
            </li>
            <li>To protect against fraud, security threats, and misuse of our services</li>
            <li>To enforce our Terms of Service and other agreements</li>
          </Bullets>
          <p>
            <Term>We do not sell your personal information to third parties.</Term>
          </p>
        </Section>

        {/* 4 */}
        <Section title="4. Legal Basis for Processing (GDPR)">
          <p>
            If you are located in the European Economic Area, United Kingdom, or other jurisdictions with similar laws,
            we process your personal information under the following legal bases:
          </p>
          <Bullets>
            <li>
              <Term>Contract:</Term> To provide the services you have requested or to take steps before entering into a
              contract
            </li>
            <li>
              <Term>Consent:</Term> Where you have given clear consent (e.g., marketing emails, optional cookies) — you
              may withdraw consent at any time
            </li>
            <li>
              <Term>Legitimate interests:</Term> For purposes such as improving our services, securing our website, and
              limited business analytics, balanced against your rights
            </li>
            <li>
              <Term>Legal obligation:</Term> Where processing is required by applicable law
            </li>
          </Bullets>
        </Section>

        {/* 5 */}
        <Section title="5. Who We Share Your Information With">
          <p>
            We share personal information only as necessary and only with parties that maintain appropriate
            confidentiality and security obligations. Specifically:
          </p>
          <Bullets>
            <li>
              Within the ANS RPO Solutions family of brands (HireEcom, Outsourcing Partner Inc., and the parent
              organization) where relevant to delivering services to you
            </li>
            <li>
              Service providers and processors acting on our behalf — including hosting providers, analytics providers
              (Google Analytics), scheduling platforms (Calendly), email service providers, customer relationship
              management systems, and secure credential-sharing tools (LastPass)
            </li>
            <li>
              Virtual assistants assigned to your account, who receive only the information necessary to deliver the
              tasks you have delegated
            </li>
            <li>
              Professional advisors including lawyers, auditors, and accountants when required for legal compliance or
              business operations
            </li>
            <li>Regulatory or government authorities where we are legally required to disclose information</li>
            <li>
              In the event of a business transfer such as a merger, acquisition, or sale of assets — in which case
              affected users will be notified
            </li>
          </Bullets>
          <p>
            We require all third parties who handle your data on our behalf to do so under written agreements that
            include confidentiality, security, and data protection obligations consistent with this policy.
          </p>
        </Section>

        {/* 6 */}
        <Section title="6. International Data Transfers">
          <p>
            Aidify operates primarily from India, with team members and clients located in the United States, United
            Kingdom, Australia, and other regions. Your information may be transferred to, stored in, and processed in
            countries outside your country of residence — including India and the United States.
          </p>
          <p>
            When we transfer personal information outside the European Economic Area or the United Kingdom, we ensure
            appropriate safeguards are in place, including Standard Contractual Clauses approved by the European
            Commission, or equivalent legal mechanisms.
          </p>
        </Section>

        {/* 7 */}
        <Section title="7. Data Retention">
          <p>
            We retain personal information only as long as necessary for the purposes described in this policy.
            Specifically:
          </p>
          <Bullets>
            <li>
              Contact form and inquiry data — up to 24 months from your last interaction, unless you become a client
            </li>
            <li>
              Client engagement records — for the duration of our engagement plus 7 years thereafter, to comply with
              Indian tax, accounting, and regulatory requirements
            </li>
            <li>Marketing and newsletter data — until you unsubscribe or request deletion</li>
            <li>
              Website analytics data — typically up to 26 months, configurable within Google Analytics retention
              settings
            </li>
            <li>Cookies — retention varies by cookie type; see Section 8 below</li>
          </Bullets>
          <p>
            When personal information is no longer needed, we either securely delete it or anonymize it so it can no
            longer be linked to you.
          </p>
        </Section>

        {/* 8 */}
        <Section title="8. Cookies and Tracking Technologies">
          <p>
            We use cookies and similar tracking technologies to make our website work, understand how it is used, and
            improve your experience.
          </p>
          <p>
            <Term>What is a cookie?</Term> A cookie is a small text file placed on your device when you visit a website.
            Cookies allow the website to recognize your device and remember information about your visit.
          </p>
          <p>
            <Term>Types of cookies we use:</Term>
          </p>
          <Bullets>
            <li>
              Strictly necessary cookies — required for the website to function (e.g., session management, security).
              These cannot be disabled.
            </li>
            <li>
              Analytics cookies — used by Google Analytics to understand visitor behavior in aggregate. These help us
              improve the site.
            </li>
            <li>Functionality cookies — remember your preferences such as language or region.</li>
            <li>
              Marketing cookies — used to deliver more relevant content and measure the effectiveness of our campaigns.
              These are set only with your consent.
            </li>
          </Bullets>
          <p>
            <Term>Third-party cookies.</Term> The following third-party services may set cookies on our site:
          </p>
          <Bullets>
            <li>
              Google Analytics — website usage analytics (Google&apos;s privacy policy:{" "}
              <Ext href="https://policies.google.com/privacy">policies.google.com/privacy</Ext>)
            </li>
            <li>
              Calendly — meeting scheduling functionality (
              <Ext href="https://calendly.com/privacy">calendly.com/privacy</Ext>)
            </li>
            <li>
              Embedded social media content (LinkedIn, Instagram, Facebook, X) — when present on a page, these platforms
              may set their own cookies
            </li>
          </Bullets>
          <p>
            <Term>Managing your cookie preferences:</Term>
          </p>
          <Bullets>
            <li>
              You can adjust your cookie preferences through the cookie banner displayed on your first visit and at any
              time via your browser settings
            </li>
            <li>
              Most browsers allow you to refuse cookies or alert you when cookies are being sent. Refer to your
              browser&apos;s help documentation for instructions
            </li>
            <li>Disabling some cookies may affect website functionality</li>
          </Bullets>
          <p>
            <Term>Cookie retention:</Term>
          </p>
          <Bullets>
            <li>Session cookies expire when you close your browser</li>
            <li>
              Persistent cookies typically last between 30 days and 24 months depending on their purpose
            </li>
          </Bullets>
        </Section>

        {/* 9 */}
        <Section title="9. Your Rights">
          <p>
            Depending on your location, you have the following rights regarding your personal information:
          </p>
          <Bullets>
            <li>
              <Term>Right of access</Term> — request a copy of the personal information we hold about you
            </li>
            <li>
              <Term>Right to rectification</Term> — ask us to correct inaccurate or incomplete information
            </li>
            <li>
              <Term>Right to erasure (&ldquo;right to be forgotten&rdquo;)</Term> — request deletion of your personal
              information, subject to legal retention requirements
            </li>
            <li>
              <Term>Right to restriction</Term> — ask us to limit how we process your information
            </li>
            <li>
              <Term>Right to data portability</Term> — request your information in a structured, machine-readable format
            </li>
            <li>
              <Term>Right to object</Term> — object to processing based on legitimate interests or direct marketing
            </li>
            <li>
              <Term>Right to withdraw consent</Term> — where processing is based on consent, you may withdraw it at any
              time without affecting prior lawful processing
            </li>
            <li>
              <Term>Right to lodge a complaint</Term> — with the relevant data protection authority in your jurisdiction
            </li>
          </Bullets>
          <p>
            Residents of California have additional rights under the CCPA, including the right to know what personal
            information is collected, the right to request deletion, and the right to opt out of the sale of personal
            information (note: Aidify does not sell personal information).
          </p>
          <p>
            To exercise any of these rights, contact us at <Email />. We will respond within 30 days, or sooner where
            required by applicable law. We may need to verify your identity before fulfilling your request.
          </p>
        </Section>

        {/* 10 */}
        <Section title="10. Data Security">
          <p>
            We take reasonable and appropriate technical and organizational measures to protect your personal
            information against unauthorized access, alteration, disclosure, or destruction. These measures include:
          </p>
          <Bullets>
            <li>Encrypted data transmission (HTTPS / TLS) across our website and services</li>
            <li>Secure credential management through enterprise tools such as LastPass</li>
            <li>Access controls limiting personal data access to authorized personnel only</li>
            <li>Background-checked virtual assistants and confidentiality agreements with all staff</li>
            <li>ISO 9001:2015 certified processes and ongoing GDPR compliance practices</li>
            <li>Regular security reviews and incident response procedures</li>
          </Bullets>
          <p>
            While we take every reasonable precaution, no method of transmission over the internet or electronic storage
            is completely secure. We cannot guarantee absolute security, but we will notify affected users and relevant
            authorities promptly in the event of a data breach as required by law.
          </p>
        </Section>

        {/* 11 */}
        <Section title="11. Children's Privacy">
          <p>
            Aidify&apos;s services are intended for businesses and professional users. We do not knowingly collect
            personal information from anyone under the age of 18. If you believe a minor has provided us with personal
            information, please contact us at <Email /> and we will take steps to delete it.
          </p>
        </Section>

        {/* 12 */}
        <Section title="12. Third-Party Links">
          <p>
            Our website may contain links to third-party websites, including our sister brands (HireEcom, Outsourcing
            Partner Inc.) and external partners. We are not responsible for the privacy practices of those sites. We
            encourage you to read their privacy policies before sharing any personal information.
          </p>
        </Section>

        {/* 13 */}
        <Section title="13. Changes to This Policy">
          <p>
            We may update this Privacy Policy from time to time to reflect changes in our practices, services, or legal
            requirements. When we make material changes, we will:
          </p>
          <Bullets>
            <li>Update the &ldquo;Last updated&rdquo; date at the top of this policy</li>
            <li>Notify registered users by email where appropriate</li>
            <li>Display a notice on our website for a reasonable period after changes take effect</li>
          </Bullets>
          <p>We encourage you to review this policy periodically.</p>
        </Section>

        {/* 14 */}
        <Section title="14. Governing Law and Jurisdiction">
          <p>
            This Privacy Policy is governed by the laws of India. Any disputes arising from or relating to this policy
            shall be subject to the exclusive jurisdiction of the courts in Delhi, India, without prejudice to your
            rights under applicable consumer protection or data protection laws in your country of residence.
          </p>
          <p>
            For users in the European Economic Area, United Kingdom, or other jurisdictions with mandatory data
            protection rights, nothing in this policy limits your ability to seek redress under your local laws.
          </p>
        </Section>

        {/* 15 */}
        <Section title="15. Contact Us">
          <p>
            If you have any questions, concerns, or requests regarding this Privacy Policy or our handling of your
            personal information, please contact us:
          </p>
          <p>
            <Term>Aidify (a brand of ANS RPO Solutions).</Term> Email: <Email />. Phone: <PhoneLink />. Address: Delhi,
            India
          </p>
        </Section>
      </div>
    </section>
  );
}
